/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import {ColumnState} from 'ag-grid-community';
import produce from 'immer';
import {getGridColumnState, onSetGridColumns, onUpdateGridColumns} from '@ion/applications/src/store/helpers';
import {PageState} from '@ion/applications/src/types/pages';
import {gridDefaultPageSize} from '@ion/applications/src/utils/helpers';
import {
  AdminAEMPConfigFilters,
  AEMPConfig
} from '@ion/core/src/types/entities';
import {ServiceError} from '@ion/core/src/types/services';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {AdminAction, AsyncState} from '@/types/redux';
import {
  ADD_ADMIN_AEMPCONFIG,
  ADD_ADMIN_AEMPCONFIG_ERROR,
  ADD_ADMIN_AEMPCONFIG_REQUEST,
  ADD_ADMIN_AEMPCONFIG_RESPONSE,
  ADMIN_AEMPCONFIG_GRID_REFRESHED,
  AUDIT_ADMIN_AEMPCONFIG,
  AUDIT_ADMIN_AEMPCONFIG_SHOW_CHANGES_DIALOG,
  DELETE_ADMIN_AEMPCONFIG,
  DELETE_ADMIN_AEMPCONFIG_ERROR,
  DELETE_ADMIN_AEMPCONFIG_REQUEST,
  DELETE_ADMIN_AEMPCONFIG_RESPONSE,
  EDIT_ADMIN_AEMPCONFIG,
  EDIT_ADMIN_AEMPCONFIG_ERROR,
  EDIT_ADMIN_AEMPCONFIG_REQUEST,
  EDIT_ADMIN_AEMPCONFIG_RESPONSE,
  GET_ADMIN_AEMPCONFIG_ERROR,
  GET_ADMIN_AEMPCONFIG_REQUEST,
  GET_ADMIN_AEMPCONFIG_RESPONSE,
  INITIALIZE_ADMIN_AEMPCONFIG,
  RESET_ADMIN_AEMPCONFIG_SEARCH_QUERIES,
  SET_GRID_COLUMNS,
  SET_GRID_PAGE_SIZE,
  UPDATE_ADMIN_AEMPCONFIG_GRID_COLUMNS,
  UPDATE_ADMIN_AEMPCONFIG_SEARCH_QUERIES,
  VIEW_ADMIN_AEMPCONFIG,
  VIEW_ADMIN_AEMPCONFIG_ERROR,
  VIEW_ADMIN_AEMPCONFIG_REQUEST,
  VIEW_ADMIN_AEMPCONFIG_RESPONSE
} from '../actions/admin-aempconfig';

export interface AdminAEMPConfigAction extends AdminAction {
  aempconfig?: AEMPConfig;
  aempconfigs?: AEMPConfig[];
  searchParams?: AdminAEMPConfigFilters;
  isView?: boolean;
};

export interface AdminAEMPConfigAuditState extends AdminAEMPConfigActionState {
  showChangesDialog?: boolean;
  auditRecord?: AuditRecordType;
};

interface AdminAEMPConfigActionState extends AsyncState {
  selected: AEMPConfig | AEMPConfig[] | null;
  result: AEMPConfig | AEMPConfig[] | null;
  isEditing: boolean;
  isView?: boolean;
};

export interface AdminAEMPConfigState extends PageState {
  aempconfigs: {
    error: ServiceError;
    loading: boolean;
  },
  grid: {
    columnState: ColumnState[],
    preferedColumnState: ColumnState[],
    hasUpdate: boolean,
    pageSize: number
  },
  add: AdminAEMPConfigActionState,
  edit: AdminAEMPConfigActionState,
  view: AdminAEMPConfigActionState,
  audit: AdminAEMPConfigAuditState,
  destroy: AdminAEMPConfigActionState,
  search: AdminAEMPConfigFilters
};

// defines the user preference key for the admin aempconfig page ...
export const PREFERENCE = 'adminAEMPConfig';

// defines the initial state for the reducer ...
export const initialState: AdminAEMPConfigState = {
  isInitialized : false,
  aempconfigs   : {
    error   : null,
    loading : false
  },
  grid : {
    columnState         : null,
    preferedColumnState : null,
    hasUpdate           : false,
    pageSize            : gridDefaultPageSize
  },
  add : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  edit : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null,
    isView    : false
  },
  view : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  audit : {
    isEditing         : false,
    selected          : null,
    loading           : false,
    error             : null,
    result            : null,
    showChangesDialog : false,
    auditRecord       : null
  },
  destroy : {
    isEditing : false,
    selected  : null,
    loading   : false,
    error     : null,
    result    : null
  },
  search : {}
};

// defines this reducers reducer functions ...
const reducers = {
  [INITIALIZE_ADMIN_AEMPCONFIG] : draft => {
    draft.isInitialized = true;
    const preference = getGridColumnState(PREFERENCE, true);
    draft.grid.columnState = preference;
    draft.grid.preferedColumnState = preference;
  },
  [SET_GRID_COLUMNS] : ({grid, aempconfigs}: AdminAEMPConfigState, {columnState}) => {
    onSetGridColumns({
      grid,
      isLoading  : aempconfigs.loading,
      preference : PREFERENCE
    }, columnState);
  },
  [UPDATE_ADMIN_AEMPCONFIG_GRID_COLUMNS] : ({grid}: AdminAEMPConfigState, {columnState}) => {
    onUpdateGridColumns({grid, preference: PREFERENCE}, columnState);
  },
  [ADMIN_AEMPCONFIG_GRID_REFRESHED] : (
    {add, edit, view, destroy}: AdminAEMPConfigState
  ) => {
    add.result = null;
    edit.result = null;
    view.result = null;
    destroy.result = null;
  },
  [GET_ADMIN_AEMPCONFIG_REQUEST] : ({aempconfigs}: AdminAEMPConfigState) => {
    aempconfigs.loading = true;
  },
  [GET_ADMIN_AEMPCONFIG_RESPONSE] : ({aempconfigs}: AdminAEMPConfigState) => {
    aempconfigs.loading = false;
  },
  [GET_ADMIN_AEMPCONFIG_ERROR] : (
    {aempconfigs}: AdminAEMPConfigState, {error}: AdminAEMPConfigAction
  ) => {
    aempconfigs.loading = false;
    aempconfigs.error   = error;
  },
  [ADD_ADMIN_AEMPCONFIG] : ({add}: AdminAEMPConfigState, {show}: AdminAEMPConfigAction) => {
    add.isEditing = show;
    add.result    = null;
  },
  [ADD_ADMIN_AEMPCONFIG_REQUEST] : ({add}: AdminAEMPConfigState) => {
    add.loading = true;
    add.error   = add.result = null;
  },
  [ADD_ADMIN_AEMPCONFIG_RESPONSE] : ({add}: AdminAEMPConfigState, {aempconfig}: AdminAEMPConfigAction) => {
    add.loading   = false;
    add.isEditing = false;
    add.result    = aempconfig;
  },
  [ADD_ADMIN_AEMPCONFIG_ERROR] : ({add}: AdminAEMPConfigState, {error}: AdminAEMPConfigAction) => {
    add.loading   = false;
    add.error     = error;
  },
  [EDIT_ADMIN_AEMPCONFIG] : ({edit}: AdminAEMPConfigState, {aempconfig, show, isView}: AdminAEMPConfigAction) => {
    edit.selected  = show ? aempconfig : null;
    edit.isEditing = show;
    edit.isView    = isView;
    edit.result    = null;
  },
  [EDIT_ADMIN_AEMPCONFIG_REQUEST] : ({edit}: AdminAEMPConfigState) => {
    edit.loading = true;
    edit.error   = edit.result = null;
  },
  [EDIT_ADMIN_AEMPCONFIG_RESPONSE] : ({edit}: AdminAEMPConfigState,
    {aempconfig}: AdminAEMPConfigAction) => {
    edit.loading   = false;
    edit.isEditing = false;
    edit.result    = aempconfig;
  },
  [EDIT_ADMIN_AEMPCONFIG_ERROR] : ({edit}: AdminAEMPConfigState, {error}: AdminAEMPConfigAction) => {
    edit.loading   = false;
    edit.error     = error;
  },
  [VIEW_ADMIN_AEMPCONFIG] : ({view}: AdminAEMPConfigState, {aempconfig, show}: AdminAEMPConfigAction) => {
    view.selected  = show ? aempconfig : null;
    view.isEditing = show;
    view.result    = null;
  },
  [VIEW_ADMIN_AEMPCONFIG_REQUEST] : ({view}: AdminAEMPConfigState) => {
    view.loading = true;
    view.error   = view.result = null;
  },
  [VIEW_ADMIN_AEMPCONFIG_RESPONSE] : ({view}: AdminAEMPConfigState, {result}: AdminAEMPConfigAction) => {
    view.loading   = false;
    view.result    = result;
  },
  [VIEW_ADMIN_AEMPCONFIG_ERROR] : ({view}: AdminAEMPConfigState, {error}: AdminAEMPConfigAction) => {
    view.loading   = false;
    view.error     = error;
  },
  [AUDIT_ADMIN_AEMPCONFIG] : ({audit}: AdminAEMPConfigState, {aempconfig, show}: AdminAEMPConfigAction) => {
    audit.selected   = show ? aempconfig : null;
    audit.isEditing = show;
  },
  [AUDIT_ADMIN_AEMPCONFIG_SHOW_CHANGES_DIALOG] : ({audit}: AdminAEMPConfigState, {auditRecord, show}) => {
    audit.showChangesDialog = show;
    audit.auditRecord       = auditRecord;
  },
  [DELETE_ADMIN_AEMPCONFIG] : ({destroy}: AdminAEMPConfigState, {aempconfig, show}: AdminAEMPConfigAction) => {
    destroy.selected  = show ? aempconfig : null;
    destroy.isEditing = show;
    destroy.result    = null;
  },
  [DELETE_ADMIN_AEMPCONFIG_REQUEST] : ({destroy}: AdminAEMPConfigState) => {
    destroy.loading = true;
    destroy.error   = destroy.result = null;
  },
  [DELETE_ADMIN_AEMPCONFIG_RESPONSE] : ({destroy}: AdminAEMPConfigState,
    {aempconfig}: AdminAEMPConfigAction) => {
    destroy.loading    = false;
    destroy.isEditing  = false;
    destroy.result     = aempconfig;
  },
  [DELETE_ADMIN_AEMPCONFIG_ERROR] : ({destroy}: AdminAEMPConfigState, {error}: AdminAEMPConfigAction) => {
    destroy.loading   = false;
    destroy.error     = error;
  },
  [UPDATE_ADMIN_AEMPCONFIG_SEARCH_QUERIES] : ({search}: AdminAEMPConfigState, {searchParams}) => {
    search.name            = searchParams.name || null;
    search.status          = searchParams.status || null;
  },
  [RESET_ADMIN_AEMPCONFIG_SEARCH_QUERIES] : ({search}: AdminAEMPConfigState) => {
    search.name            = null;
    search.status          = null;
  },
  [SET_GRID_PAGE_SIZE] : ({grid}: AdminAEMPConfigState, {pageSize}) => {
    grid.pageSize = pageSize;
  }
};

/**
 * defines all reducers for actions of interest to the this reducer ...
 */
export default produce((draft: AdminAEMPConfigState = initialState, action) => {
  reducers?.[action.type]?.(draft, action);
  return draft;
});
